import React, { useMemo } from 'react';
import {
  Archive,
  CheckThick,
  ChevronLeft,
  IconFormIcons,
  Target,
} from 'shared/assets/icons';
import { GoalContextMenu } from 'shared/components/connected/goal-context-menu';
import { CircleProgress } from 'shared/components/ui/circle-progress';
import { Icon } from 'shared/components/ui/icon';
import { Colors, IconButton, Sizes } from 'shared/components/ui/icon-button';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { Paths } from 'shared/routes';
import { DataType } from 'shared/types/data-type';
import { Goal } from 'shared/types/goal';
import { LifeArea } from 'shared/types/life-area';
import {
  goalInboxID,
  overdueSectionID,
  unlinkedPlannedTasksID,
} from 'shared/types/orderable-section';
import { getCompletionPercentage } from 'shared/utils/get-completion-percentage';
import { getGoalCompletion } from 'shared/utils/get-goal-completion';
import { getLinkedItemColor } from 'shared/utils/get-linked-item-color';
import { replaceUrlParams } from 'shared/utils/replace-url-params';

import { Deadline } from './deadline';
import * as Styled from './goal-item-card.style';

enum Illustration {
  Archived = 'archived',
  CompletionPercentage = 'completionPercentage',
  Icon = 'icon',
}

export type GoalItemCardProps = {
  goal: Goal;

  lifeAreas: LifeArea[];
  goals: Goal[];
  childCount?: number;

  onCollapse?: (e: React.MouseEvent) => void;
  collapsed?: boolean;
  isGrabbable?: boolean;
  isGrabbed?: boolean;
  showMissingDates?: boolean;
};

export const GoalItemCard: React.FC<GoalItemCardProps> = ({
  goal,
  lifeAreas,
  goals,
  childCount,
  onCollapse,
  collapsed,
  isGrabbable,
  isGrabbed,
  showMissingDates,
}) => {
  const {
    open: openMenu,
    close: closeMenu,
    position: menuPosition,
  } = useContextMenuState({
    disabled: !!goal.frozenAt,
  });

  const _onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCollapse?.(e);
  };

  const color = useMemo(
    () => getLinkedItemColor(goal.id, { goals, lifeAreas }),
    [goal.id, goals, lifeAreas],
  );

  const completionPercentage = useMemo(() => {
    const { start, end, current } = getGoalCompletion(goal, {
      goals,
    });
    return end !== Infinity
      ? getCompletionPercentage({ start, end, current })
      : undefined;
  }, [goal, goals]);

  const isOverdueOrInbox = [goalInboxID, overdueSectionID].includes(goal.id);

  const isFakeGoal = isOverdueOrInbox || unlinkedPlannedTasksID === goal.id;

  const illustrationToDisplay = goal.archivedAt
    ? Illustration.Archived
    : isFakeGoal
      ? Illustration.Icon
      : Illustration.CompletionPercentage;

  return (
    <>
      <Styled.Container
        $isGrabbable={!!isGrabbable}
        $isGrabbed={!!isGrabbed}
        onClick={_onClick}
        onContextMenu={openMenu}
        data-component-type={DataType.Goal}
        data-id={goal.id}
        data-title={goal.name}
      >
        <Styled.Content
          $completed={!!goal.completedAt}
          $archived={!!goal.archivedAt}
        >
          <Styled.IllustrationContainer
            $color={
              illustrationToDisplay !== Illustration.CompletionPercentage
                ? color
                : undefined
            }
          >
            {illustrationToDisplay === Illustration.CompletionPercentage && (
              <Styled.ProgressContainer $color={color}>
                <CircleProgress
                  percentage={completionPercentage}
                  hidePercentage
                  isSmall
                />
              </Styled.ProgressContainer>
            )}

            {[Illustration.Icon, Illustration.Archived].includes(
              illustrationToDisplay,
            ) && (
              <Styled.IconContainer
                $error={goal.id === overdueSectionID}
                $noBackground={
                  isFakeGoal || illustrationToDisplay === Illustration.Archived
                }
              >
                <Icon
                  icon={
                    illustrationToDisplay === Illustration.Archived
                      ? Archive
                      : IconFormIcons[goal.iconName!] ?? Target
                  }
                />
              </Styled.IconContainer>
            )}

            {!!goal.completedAt && (
              <Styled.CompletedContainer>
                <Icon icon={CheckThick} />
              </Styled.CompletedContainer>
            )}
          </Styled.IllustrationContainer>

          <Styled.TitleContainer
            $completed={!!goal.completedAt}
            to={
              isFakeGoal
                ? ''
                : replaceUrlParams(Paths.GoalDetail, { id: goal.id })
            }
            as={isFakeGoal ? 'span' : undefined}
          >
            <Styled.Title $isH6={isOverdueOrInbox}>
              {goal.name}
              {isOverdueOrInbox && !!childCount && (
                <Styled.Badge>{childCount}</Styled.Badge>
              )}
            </Styled.Title>
            {!isFakeGoal && (
              <Styled.ChevronContainer>
                <Icon icon={ChevronLeft} />
              </Styled.ChevronContainer>
            )}
          </Styled.TitleContainer>
        </Styled.Content>

        <Styled.MetaContainer>
          {!goal.completedAt && !goal.archivedAt && (
            <Deadline goal={goal} showMissingDates={showMissingDates} />
          )}

          {!!onCollapse && (
            <IconButton
              size={Sizes.Small}
              icon={ChevronLeft}
              rotation={collapsed ? -90 : 0}
              color={Colors.Default}
              as="span"
            />
          )}
        </Styled.MetaContainer>
      </Styled.Container>

      {!!menuPosition && (
        <GoalContextMenu
          id={goal.id}
          onClose={closeMenu}
          location={menuPosition}
        />
      )}
    </>
  );
};
